import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCheck, Download, ExternalLink, Inbox, MailOpen, Mail, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { EnvKey } from '@/app/site/scope';
import catalog from '@/features/catalog/Catalog.module.scss';
import type { FieldBody, RecordBody, TypeBody } from '@/shared/api/generated/model';
import {
  recordsDelete,
  recordsList,
  recordsMarkRead,
} from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { cx } from '@/shared/lib/cx';
import { describeWhen } from '@/shared/lib/dates';
import { pluralize } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Segmented } from '@/shared/ui/Segmented';
import { toast } from '@/shared/ui/Toast';

import { StageActions } from '@/features/stages/StageActions';
import { StageBadge } from '@/features/stages/StageBadge';
import { hasStages, stageOf } from '@/features/stages/stages';
import { Kanban } from '@/features/stages/Kanban';
import { useViewMode } from '@/features/catalog/useViewMode';

import styles from './FormsPage.module.scss';
import { submissionFields, submissionsCSV, valueText } from './submissions';
import { SUBMISSIONS } from './words';

const PER_PAGE = 50;
/** Сколько полей видно в строке таблицы; остальное — в окне заявки. */
const COLUMNS = 3;

export interface SubmissionsProps {
  siteId: string;
  env: EnvKey;
  type: TypeBody;
  fields: FieldBody[];
  /** Может ли человек убирать заявки в корзину. */
  canDelete: boolean;
}

/**
 * Заявки формы: новые сверху отмечены точкой; открыть — прочитать. Отметка
 * «новая» общая для команды: прочитал один — прочитано у всех.
 */
export function Submissions({ siteId, env, type, fields, canDelete }: SubmissionsProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const staged = hasStages(type.workflow);
  const [view, setView] = useViewMode(`form-${type.id}`);
  const board = staged && view === 'list';
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<RecordBody>();
  const [exporting, setExporting] = useState(false);

  const shown = submissionFields(fields);
  const columns = shown.slice(0, COLUMNS);

  const list = useQuery({
    queryKey: ['records', type.id, 'submissions', filter, page],
    queryFn: async () => {
      const response = await recordsList(type.id, {
        sort: 'created_at',
        order: 'desc',
        page,
        per_page: PER_PAGE,
        ...(filter === 'unread' ? { unread: true } : {}),
      });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список заявок');
      }

      return response.data;
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['records', type.id] });
    void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
    void queryClient.invalidateQueries({ queryKey: ['content-type', type.id] });
  };

  const mark = (ids: string[] | undefined, read: boolean) =>
    recordsMarkRead(type.id, { ...(ids === undefined ? {} : { ids }), read })
      .then(refresh)
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось отметить');
      });

  const openSubmission = (record: RecordBody) => {
    setOpen(record);

    if (record.read_at === undefined) {
      void mark([record.id], true);
    }
  };

  const remove = (record: RecordBody) => {
    recordsDelete(record.id)
      .then(() => {
        toast.success('Заявка в корзине');
        setOpen(undefined);
        refresh();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось убрать');
      });
  };

  // Выгрузка — все заявки формы, страницами по 100: так её можно открыть в
  // Excel или отдать менеджеру.
  const download = async () => {
    setExporting(true);

    try {
      const all: RecordBody[] = [];

      for (let next = 1; ; next++) {
        const response = await recordsList(type.id, {
          sort: 'created_at',
          order: 'desc',
          page: next,
          per_page: 100,
        });

        if (response.status !== 200) {
          throw new Error('Неожиданный ответ на список заявок');
        }

        all.push(...(response.data.items ?? []));

        if (all.length >= response.data.total || (response.data.items ?? []).length === 0) {
          break;
        }
      }

      const blob = new Blob([submissionsCSV(shown, all)], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);
      link.download = `${type.slug}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (cause: unknown) {
      toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось выгрузить');
    } finally {
      setExporting(false);
    }
  };

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className={styles['submissions']}>
      <div className={catalog['bar']}>
        <Segmented
          value={filter}
          onValueChange={(next) => {
            setFilter(next as typeof filter);
            setPage(1);
          }}
          aria-label="Какие заявки показать"
          options={[
            { value: 'all', label: `Все · ${String(type.records_count)}` },
            { value: 'unread', label: `Новые · ${String(type.unread_count)}` },
          ]}
        />
        <div className={styles['bar__actions']}>
          {staged && (
            <Segmented
              value={board ? 'kanban' : 'table'}
              onValueChange={(next) => {
                setView(next === 'kanban' ? 'list' : 'grid');
              }}
              aria-label="Как показывать"
              options={[
                { value: 'table', label: 'Таблица' },
                { value: 'kanban', label: 'Канбан' },
              ]}
            />
          )}
          {type.unread_count > 0 && (
            <Button
              variant="ghost"
              tone="neutral"
              iconLeft={<CheckCheck size={15} />}
              onClick={() => {
                void mark(undefined, true);
              }}
            >
              Прочитать все
            </Button>
          )}
          {type.records_count > 0 && (
            <Button
              variant="outline"
              tone="neutral"
              iconLeft={<Download size={15} />}
              loading={exporting}
              onClick={() => {
                void download();
              }}
            >
              CSV
            </Button>
          )}
        </div>
      </div>

      {board ? (
        <Kanban
          type={type}
          onOpen={openSubmission}
          subtitle={(record) =>
            `${describeWhen(record.created_at)}${record.read_at === undefined ? ' · новая' : ''}`
          }
        />
      ) : list.isPending ? null : items.length === 0 ? (
        <EmptyState
          compact
          icon={<Inbox />}
          title={filter === 'unread' ? 'Новых заявок нет' : 'Заявок пока нет'}
          description={
            filter === 'unread'
              ? 'Все заявки прочитаны.'
              : 'Как подключить форму на сайте — на вкладке «API».'
          }
        />
      ) : (
        <>
          <div className={catalog['table']}>
            <table>
              <thead>
                <tr>
                  <th aria-label="Новая" />
                  <th>Когда</th>
                  {staged && <th>Статус</th>}
                  {columns.map((field) => (
                    <th key={field.key}>{field.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((record) => (
                  <tr
                    key={record.id}
                    className={cx(
                      styles['row'],
                      record.read_at === undefined && styles['row--new'],
                    )}
                    tabIndex={0}
                    onClick={() => {
                      openSubmission(record);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        openSubmission(record);
                      }
                    }}
                  >
                    <td className={styles['dot__cell']}>
                      {record.read_at === undefined && (
                        <span className={styles['dot']} aria-label="Новая" />
                      )}
                    </td>
                    <td className={styles['when']}>{describeWhen(record.created_at)}</td>
                    {staged && (
                      <td>
                        <StageBadge status={stageOf(type.workflow, record.stage)} />
                      </td>
                    )}
                    {columns.map((field) => (
                      <td key={field.key} className={styles['cell']}>
                        {valueText(record.data[field.key]) || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className={styles['pager']}>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={page <= 1}
                onClick={() => {
                  setPage(page - 1);
                }}
              >
                Новее
              </Button>
              <span>
                {page} из {pages} · {pluralize(total, SUBMISSIONS)}
              </span>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={page >= pages}
                onClick={() => {
                  setPage(page + 1);
                }}
              >
                Старше
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog
        open={open !== undefined}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(undefined);
          }
        }}
      >
        {open !== undefined && (
          <DialogContent
            title={open.title || 'Заявка'}
            description={`Пришла ${describeWhen(open.created_at)}`}
            size="md"
            footer={
              <div className={styles['dialog__actions']}>
                <Button
                  variant="ghost"
                  tone="neutral"
                  iconLeft={<Mail size={15} />}
                  onClick={() => {
                    void mark([open.id], false).then(() => {
                      setOpen(undefined);
                    });
                  }}
                >
                  Отметить новой
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  tone="neutral"
                  iconLeft={<ExternalLink size={15} />}
                >
                  <Link
                    to="/sites/$siteId/$env/r/$recordId"
                    params={{ siteId, env, recordId: open.id }}
                  >
                    Править
                  </Link>
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    tone="danger"
                    iconLeft={<Trash2 size={15} />}
                    onClick={() => {
                      remove(open);
                    }}
                  >
                    В корзину
                  </Button>
                )}
              </div>
            }
          >
            {staged && (
              <div className={styles['stage']}>
                <StageActions
                  record={open}
                  workflow={type.workflow}
                  onChanged={(next) => {
                    setOpen(next);
                  }}
                />
              </div>
            )}
            <dl className={styles['details']}>
              {shown.map((field) => (
                <div key={field.key} className={styles['details__row']}>
                  <dt>{field.name}</dt>
                  <dd>
                    <Value field={field} value={open.data[field.key]} />
                  </dd>
                </div>
              ))}
            </dl>
            <p className={cx(styles['muted'], styles['hint'])}>
              <MailOpen size={13} /> Открытая заявка отмечается прочитанной у всей команды.
            </p>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

/** Значение в окне заявки: почта, телефон и адрес — ссылками. */
function Value({ field, value }: { field: FieldBody; value: unknown }) {
  const text = valueText(value);

  if (text === '') {
    return <span className={styles['muted']}>—</span>;
  }

  if (field.widget === 'email') {
    return <a href={`mailto:${text}`}>{text}</a>;
  }

  if (field.widget === 'phone') {
    return <a href={`tel:${text.replace(/[^\d+]/g, '')}`}>{text}</a>;
  }

  if (/^https?:\/\//.test(text)) {
    return (
      <a href={text} target="_blank" rel="noreferrer noopener">
        {text}
      </a>
    );
  }

  return <span className={styles['pre']}>{text}</span>;
}
